import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FadeInUp, StaggeredChildren, StaggeredItem } from "./PageTransition";

const testimonials = [
  {
    id: 1,
    name: "Rajesh Kumar",
    company: "Digital Solutions Inc.",
    role: "CEO",
    content: "Tech ADR transformed our business with their innovative web solutions. The team's expertise and dedication exceeded our expectations. Our online presence has never been stronger!",
    rating: 5,
    avatar: "👨‍💼",
    color: "from-blue-500 to-cyan-500"
  },
  {
    id: 2,
    name: "Priya Sharma",
    company: "StartupHub",
    role: "Founder",
    content: "Working with Tech ADR was a game-changer for our startup. They delivered our MVP faster than expected and provided excellent ongoing support. Highly recommended!",
    rating: 5,
    avatar: "👩‍💻",
    color: "from-purple-500 to-pink-500"
  },
  {
    id: 3,
    name: "Amit Patel",
    company: "Local Business Network",
    role: "Owner",
    content: "Tech ADR helped us establish a strong digital presence. Their cost-effective solutions and local language support made the entire process seamless and professional.",
    rating: 5,
    avatar: "👨‍🏭",
    color: "from-green-500 to-emerald-500"
  },
  {
    id: 4,
    name: "Sneha Reddy",
    company: "E-commerce Plus",
    role: "Marketing Director",
    content: "The digital marketing services from Tech ADR significantly increased our online visibility. Their strategic approach and data-driven insights delivered measurable results.",
    rating: 5,
    avatar: "👩‍🎨",
    color: "from-orange-500 to-red-500"
  },
  {
    id: 5,
    name: "Vikram Singh",
    company: "Tech Innovations",
    role: "CTO",
    content: "Tech ADR's mobile app development expertise is outstanding. They delivered a high-quality app that our users love. The integration services were flawless.",
    rating: 5,
    avatar: "👨‍🔬",
    color: "from-teal-500 to-green-500"
  }
];

function StarRating({ rating }) {
  return (
    <div className="flex space-x-1">
      {[...Array(5)].map((_, i) => (
        <motion.span
          key={i}
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: i * 0.1, duration: 0.3 }}
          className={`text-lg ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
        >
          ⭐
        </motion.span>
      ))}
    </div>
  );
}

function TestimonialCard({ testimonial, isActive, index, dark }) {
  return (
    <motion.div
      className={`relative w-full max-w-md mx-auto ${
        isActive ? 'z-20' : 'z-10'
      }`}
      initial={{ opacity: 0, scale: 0.8, rotateY: -45 }}
      animate={{ 
        opacity: isActive ? 1 : 0.6, 
        scale: isActive ? 1 : 0.9,
        rotateY: isActive ? 0 : -15,
        y: isActive ? 0 : 20
      }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      whileHover={isActive ? { 
        scale: 1.05, 
        rotateY: 5,
        transition: { duration: 0.3 }
      } : {}}
    >
      <div className={`relative rounded-2xl p-8 shadow-2xl backdrop-blur-md border-2 transition-all duration-500 ${
        dark 
          ? 'bg-[#23244d]/90 border-indigo-400/30 hover:border-indigo-400/60' 
          : 'bg-white/90 border-indigo-500/30 hover:border-indigo-500/60'
      }`}>
        {/* Background gradient */}
        <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br opacity-10 ${
          testimonial.color
        }`} />
        
        {/* Quote icon */}
        <motion.div
          className="absolute -top-4 -left-4 w-12 h-12 rounded-full flex items-center justify-center text-2xl shadow-lg"
          style={{
            background: `linear-gradient(135deg, var(--tw-gradient-stops))`,
            '--tw-gradient-from': testimonial.color.split('-')[1] === '500' ? '#3b82f6' : '#60a5fa',
            '--tw-gradient-to': testimonial.color.split('-')[3] === '500' ? '#06b6d4' : '#22d3ee'
          }}
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          💬
        </motion.div>

        {/* Content */}
        <div className="relative z-10">
          <p className={`text-lg leading-relaxed mb-6 italic ${
            dark ? 'text-white/90' : 'text-[#23244d]/90'
          }`}>
            "{testimonial.content}"
          </p>
          
          <div className="flex items-center space-x-4">
            <div className="text-4xl">{testimonial.avatar}</div>
            <div className="flex-1">
              <h4 className={`font-bold text-lg ${
                dark ? 'text-white' : 'text-[#1b1c4e]'
              }`}>
                {testimonial.name}
              </h4>
              <p className={`text-sm ${
                dark ? 'text-white/70' : 'text-[#23244d]/70'
              }`}>
                {testimonial.role} at {testimonial.company}
              </p>
              <div className="mt-2">
                <StarRating rating={testimonial.rating} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Testimonials({ dark }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const carouselRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!isDragging) {
        setDirection(1);
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      }
    }, 5000);

    return () => clearInterval(timer);
  }, [isDragging]);

  const goToNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const goToPrevious = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  // Touch/Mouse event handlers
  const handleStart = (clientX) => {
    setIsDragging(true);
    setStartX(clientX);
    setCurrentX(clientX);
  };

  const handleMove = (clientX) => {
    if (isDragging) {
      setCurrentX(clientX);
    }
  };

  const handleEnd = () => {
    if (isDragging) {
      const diff = startX - currentX;
      const threshold = 50; // Minimum swipe distance

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          goToNext(); // Swipe left
        } else {
          goToPrevious(); // Swipe right
        }
      }
      
      setIsDragging(false);
      setStartX(0);
      setCurrentX(0);
    }
  };

  // Touch events
  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  // Mouse events
  const handleMouseDown = (e) => {
    handleStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    handleEnd();
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      handleEnd();
    }
  };

  return (
    <section className="relative py-20 px-4 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-indigo-900/20 dark:via-[#1b1c4e]/30 dark:to-purple-900/20" />
      
      <div className="relative max-w-6xl mx-auto">
        <FadeInUp className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1b1c4e] dark:text-white mb-4">
            What Our Clients Say
          </h2>
          <p className="text-lg text-[#23244d] dark:text-white/80 max-w-2xl mx-auto">
            Don't just take our word for it - hear from the businesses we've helped transform
          </p>
        </FadeInUp>

        {/* Carousel */}
        <div 
          ref={carouselRef}
          className={`relative cursor-grab active:cursor-grabbing swipe-container ${isDragging ? 'dragging' : ''}`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          <div className="flex justify-center items-center min-h-[400px]">
            <AnimatePresence mode="wait">
              <TestimonialCard
                key={currentIndex}
                testimonial={testimonials[currentIndex]}
                isActive={true}
                index={currentIndex}
                dark={dark}
              />
            </AnimatePresence>
          </div>

          {/* Navigation buttons */}
          <motion.button
            onClick={goToPrevious}
            className={`absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full shadow-lg backdrop-blur-md border-2 transition-all duration-300 ${
              dark
                ? 'bg-[#23244d]/80 border-indigo-400/50 hover:bg-[#23244d] hover:border-indigo-400'
                : 'bg-white/80 border-indigo-500/50 hover:bg-white hover:border-indigo-500'
            }`}
            whileHover={{ scale: 1.1, x: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-6 h-6 mx-auto text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </motion.button>

          <motion.button
            onClick={goToNext}
            className={`absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full shadow-lg backdrop-blur-md border-2 transition-all duration-300 ${
              dark
                ? 'bg-[#23244d]/80 border-indigo-400/50 hover:bg-[#23244d] hover:border-indigo-400'
                : 'bg-white/80 border-indigo-500/50 hover:bg-white hover:border-indigo-500'
            }`}
            whileHover={{ scale: 1.1, x: 2 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-6 h-6 mx-auto text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </motion.button>
        </div>

        {/* Dots indicator */}
        <div className="flex justify-center space-x-2 mt-8 testimonial-dots">
          {testimonials.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => goToSlide(index)}
              style={{
                width: window.innerWidth <= 768 ? 8 : 12,
                height: window.innerWidth <= 768 ? 8 : 12,
                transition: 'width 0.2s, height 0.2s',
              }}
              className={`rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-indigo-600 dark:bg-indigo-400' // Remove scale-125
                  : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            />
          ))}
        </div>

        {/* Swipe hint for mobile */}
        <div className="flex justify-center mt-4 md:hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            transition={{ delay: 1, duration: 0.5 }}
            className={`text-sm ${
              dark ? 'text-white/60' : 'text-[#23244d]/60'
            }`}
          >
            ← Swipe to navigate →
          </motion.div>
        </div>
      </div>
    </section>
  );
} 